import React, { useState, useEffect, useContext } from 'react';
import { PropsWithChildren } from 'react';
import agent from '../agent';
import { UserContext, UserContextType, isStaffType } from './UserContext';
import {
  JobOwnerDetailViewType,
  PartnerJobDetailType,
  PartnerJobViewType,
} from '../types/services-api';
import { JobPostingCSVJobType, JobPostingCSVPartnerType } from '../types/csv';
import dayjs from 'dayjs';
import { defaultJobSearchParams } from '../components/job-search/JobSearchForm';
import { AuthenticatedDpJobViewType } from '../components/job-search/JobSearchJobDetail';
import { UnauthenticatedDpJobViewType } from '../templates/jobs/job-post-catch-all';
import { JobDetailViewType } from '../components/job-postings/JobDetailLayout';
import { jobTypes } from '../utils/displayHelper';
import { transformJobDetailParams } from '../utils/jobDetailTransform';

export type JobSearchParamsType = {
  'days-old'?: string;
  endDate?: string;
  keyword?: string;
  position?: string;
  radius?: string;
  startDate?: string;
  type?: string;
  zip?: string;
  evaluation?: string;
};

export type JobSearchContextType = {
  defaultMapCenter: { lat: string; lng: string } | null;
  searchJobs: (searchParams: JobSearchParamsType) => void;
  localJobs: JobDetailViewType[] | undefined;
  nationalJobs: JobDetailViewType[] | undefined;
  partnerJobs: JobDetailViewType[] | undefined;
  updateJob: (updatedValues: JobDetailViewType) => void;
  searchErrorMessage: string;
  jobSearchParams?: JobSearchParamsType;
  updateJobSearchParams: (updatedValues: JobSearchParamsType) => void;
};

export const JobSearchContext = React.createContext<JobSearchContextType>({
  defaultMapCenter: null,
  searchJobs: () => {},
  localJobs: undefined,
  nationalJobs: undefined,
  updateJob: () => {},
  partnerJobs: undefined,
  searchErrorMessage: '',
  jobSearchParams: undefined,
  updateJobSearchParams: () => {},
});

const JobSearchContextWrapper = (props: PropsWithChildren) => {
  const { user } = useContext(UserContext) as UserContextType;
  const [localJobs, setLocalJobs] = useState<JobDetailViewType[] | undefined>(
    undefined
  );
  const [nationalJobs, setNationalJobs] = useState<
    JobDetailViewType[] | undefined
  >(undefined);
  const [partnerJobs, setPartnerJobs] = useState<
    JobDetailViewType[] | undefined
  >(undefined);
  const [searchErrorMessage, setSearchErrorMessage] = useState('');
  const [jobSearchParams, setJobSearchParams] = useState<JobSearchParamsType>();
  const [defaultMapCenter, setDefaultMapCenter] = useState<{
    lat: string;
    lng: string;
  } | null>(null);

  useEffect(() => {
    if (isStaffType(user)) {
      setDefaultMapCenter({ lat: user.lat, lng: user.lng });
      if (!jobSearchParams && !!user.position && !!user.zip) {
        updateJobSearchParams({
          ...defaultJobSearchParams,
          position: user.position,
          zip: user.zip,
        });
      }
    }
  }, [user]);

  useEffect(() => {
    if (!!jobSearchParams && jobSearchParams.zip && jobSearchParams.position) {
      searchJobs();
    }
  }, [jobSearchParams]);

  function updateJobSearchParams(updatedValues: JobSearchParamsType) {
    setJobSearchParams({ ...jobSearchParams, ...updatedValues });
  }

  function searchJobs() {
    if (!!jobSearchParams) {
      setLocalJobs(undefined);
      setNationalJobs(undefined);
      setPartnerJobs(undefined);
      setSearchErrorMessage('');
      //distance calculations will default to be from users lat/lng address instead of overall zip
      //only pass zip to api if different from their home address zip
      let transformedParams = {
        ...jobSearchParams,
        radius:
          jobSearchParams.radius === '101' ? '200' : jobSearchParams.radius,
      };
      agent.Jobs.search(transformedParams)
        .then((res) => {
          setLocalJobs(
            res.data.premiumJobs
              .map((job) => transformJobDetailParams(job))
              .filter((job) => {
                const evalFilter =
                  jobSearchParams.evaluation === 'LIKED'
                    ? job.isLike
                    : jobSearchParams.evaluation === 'DISLIKED'
                      ? job.isDislike
                      : true;
                const startDateFilter = !!jobSearchParams.startDate
                  ? job.jobDates?.some(
                      (date) =>
                        !dayjs(date).isBefore(dayjs(jobSearchParams.startDate))
                    )
                  : true;
                const endDateFilter = !!jobSearchParams.endDate
                  ? job.jobDates?.some(
                      (date) =>
                        !dayjs(date).isAfter(dayjs(jobSearchParams.endDate))
                    )
                  : true;
                return evalFilter && startDateFilter && endDateFilter;
              })
          );
          if (transformedParams.radius === '101') {
            setNationalJobs(
              res.data.premiumJobs
                .map((job) => transformJobDetailParams(job))
                .filter((job) => job.isNational)
            );
          } else {
            getNationalJobs();
          }
          setPartnerJobs(
            res.data.partnerJobs.map((job) => transformJobDetailParams(job))
          );
        })
        .catch((err) => {
          if (err && err.message) {
            setSearchErrorMessage(err.message);
          } else {
            setSearchErrorMessage('Failed to search for jobs');
          }
        });
    }
  }

  function getNationalJobs() {
    agent.Jobs.search({ ...jobSearchParams, radius: '101' })
      .then((res) => {
        setNationalJobs(
          res.data.premiumJobs
            .map((job) => transformJobDetailParams(job))
            .filter((job) => job.isNational)
        );
      })
      .catch((err) => {
        if (err && err.message) {
          setSearchErrorMessage(err.message);
        } else {
          setSearchErrorMessage('Failed to search for national jobs');
        }
      });
  }

  function updateJob(updatedItem: Partial<JobDetailViewType>) {
    const updatedLocalJobs = localJobs?.map((pj) => {
      return pj.jobId === updatedItem.jobId
        ? {
            ...pj,
            ...updatedItem,
          }
        : pj;
    });
    setLocalJobs(updatedLocalJobs);

    const updatedNationalJobs = nationalJobs?.map((pj) => {
      return pj.jobId === updatedItem.jobId
        ? {
            ...pj,
            ...updatedItem,
          }
        : pj;
    });

    setNationalJobs(updatedNationalJobs);

    const updatedPartnerJobs = partnerJobs?.map((ej) => {
      return ej.jobId === updatedItem.jobId
        ? {
            ...ej,
            ...updatedItem,
          }
        : ej;
    });
    setPartnerJobs(updatedPartnerJobs);
  }

  JobSearchContext.displayName = 'Job Search Context';

  return (
    <JobSearchContext.Provider
      value={{
        defaultMapCenter,
        jobSearchParams,
        searchJobs,
        localJobs,
        nationalJobs,
        partnerJobs,
        updateJob,
        searchErrorMessage,
        updateJobSearchParams,
      }}
    >
      {props.children}
    </JobSearchContext.Provider>
  );
};

export default JobSearchContextWrapper;

export function isDpAuthJob(obj: any): obj is AuthenticatedDpJobViewType {
  return 'jobId' in obj && 'appliedDate' in obj;
}

export function isDpCsvJob(obj: any): obj is JobPostingCSVJobType {
  return 'jobId' in obj && 'occupationalCategory' in obj;
}

export function isDpUnauthJob(obj: any): obj is UnauthenticatedDpJobViewType {
  return 'jobId' in obj && 'uri' in obj;
}

export function isPartnerAuthJob(obj: any): obj is PartnerJobDetailType {
  return 'jobKey' in obj && 'distance' in obj;
}

export function isPartnerCsvJob(obj: any): obj is JobPostingCSVPartnerType {
  return 'jobKey' in obj && !('distance' in obj);
}

export function isPartnerUnauthJob(obj: any): obj is PartnerJobViewType {
  return 'jobKey' in obj && !('distance' in obj);
}

export { transformJobDetailParams };
